import { useParams } from 'react-router-dom'; // react-router **dom**
import SideBar from '../../components/global/SideBar';
import './index.css';
import {  useEffect, useState } from 'react';
import { usePageTitle } from '../../hooks/usePageTitle';
import { TABS, Tabs } from '../../components/programs-profile/Tabs';
import { Start } from '../../components/programs-profile/Start';
import { Materials } from '../../components/programs-profile/Materials';
import { VideosList } from '../../components/programs-profile/VideosList';
import type { UserProgram } from '../../types/userProgram.interface';
import { Modal } from '@beholos/holoscommon';
import StartProgramModal from '../../components/programs-profile/StartProgramModal';
import type { ProgramInterface } from '../../types/program.interface';
import { createReview, enrollInProgram, fetchProgramBySlug, type FetchProgramBySlugResponse } from '../../services/program.service';
import type { Comment } from "../../components/programs-profile/CommentsList";
import { ProgramCompleteModal } from '../../components/programs-profile/ProgramCompleteModal';

export default function ProgramsProfile() {
  const { programSlug } = useParams();

  const [program, setProgram] = useState<ProgramInterface | null>(null);
  const [userProgram, setUserProgram] = useState<UserProgram | undefined>(undefined);
  const [reviews, setReviews] = useState<Comment[]>([]);
  const [programReview, setProgramReview] = useState<{ rating: number; review: string }>({ rating: 0, review: '' });
  const [otherPrograms, setOtherPrograms] = useState<ProgramInterface[]>([]);
  const [activeTab, setActiveTab] = useState(TABS[0]);
  const [openEnroll, setOpenEnroll] = useState(false)
  const [openCompleted, setOpenCompleted] = useState(false)
  const [loadingStartProgram, setLoadingStartProgram] = useState(false)
  const [message, setMessage] = useState('')

  usePageTitle(program?.name ? `${program.name} - Holos` : 'Programa - Holos');

  useEffect(() => {
    loadData();
  }, []);

  const handleCreateReview = () => {
    if (programReview.rating > 0 && programReview.review) {
      createReview(program?.id || '', programReview.rating - 1, programReview.review)
        .then((message) => {
          setMessage(message);
          setOpenCompleted(false);
          loadData();
        })
        .catch((error) => {
          console.error('Error creating review:', error);
          setMessage('Algo salió mal al enviar tu reseña. Inténtalo de nuevo más tarde.');
        });
    }
  };

  const loadData = () => {
    fetchProgramBySlug(programSlug || '')
      .then((data: FetchProgramBySlugResponse | null) => {
        console.log('Fetched program data:', data);
        if (data) {
          setProgram(data.program);
          setUserProgram(data.userProgram);
          setReviews(data.reviews);
          setOtherPrograms(data.otherPrograms);
          const showProgramCompleted = !!(data.programComplete && (!data.userProgram?.programCompleteShown || !data?.userProgram?.reviewGiven));
          setOpenCompleted(showProgramCompleted);
          if (showProgramCompleted) {
            setProgramReview(data.programReview || { rating: -1, review: '' });
          }
        } else {
          setMessage('Program not found');
        }
      })
      .catch((error) => {
        console.error('Error fetching program:', error);
        setMessage('Error fetching program');
      });
  }

  const signUp = () => {
    setLoadingStartProgram(true);
    setActiveTab(TABS[0]);
    enrollInProgram(program?.id || '')
      .then((message) => {
        setMessage(message);
        if (message === 'Tu programa esta registrando.') {
          setOpenEnroll(false)
        }
      })
      .catch((error) => {
        console.error('Error enrolling in program:', error);
        setMessage(message);
      })
      .finally(() => {
        setLoadingStartProgram(false);
        loadData();
      });
  }

  const openEnrollModal = () => {
    setMessage('');
    setOpenEnroll(true);
  }

  return (
    <div className="flex h-[calc(100vh-80px)] w-screen overflow-hidden">
      <div className="hidden md:block md:w-80 lg:w-96 h-full overflow-y-auto">
        <SideBar />
      </div>

      {program && <div className="flex-1 h-full overflow-y-auto bg-gray-100">
        <div>
          <div
            className="min-w-full h-full relative py-50 bg-no-repeat bg-cover bg-right min-h-[calc(100vh-80px)] md:min-h-[auto]"
            style={{ backgroundImage: `url(https://image.beholos.com/fit-in/1600x750${program.bannerImage})` }}
          >
            <div className="absolute inset-0 bg-black/30 z-0" />

            <div className="relative z-10 flex items-center h-full px-8">
              <div className="space-y-4 max-w-lg">
                <h2 className="text-5xl font-medium text-white font-juana italic">
                  {program.name}
                </h2>
                <h5 className="text-xl leading-normal text-white font-juana">
                  {program.shortDescription}
                </h5>
              </div>
            </div>
          </div>

          <Tabs active={activeTab} onChange={setActiveTab} />

          <div className="max-w-6xl mx-auto py-5 px-8">
            {activeTab === TABS[0] && <Start
              program={program}
              userProgram={userProgram}
              otherPrograms={otherPrograms}
              setActiveTab={setActiveTab}
              reviews={reviews}
              setOpenEnroll={openEnrollModal}
            />}
            {activeTab === TABS[1] && <VideosList program={program} userProgram={userProgram} />}
            {activeTab === TABS[2] && <Materials
              program={program}
              userProgram={userProgram}
              setOpenEnroll={openEnrollModal}
            />}

            <Modal isOpen={openEnroll} onClose={() => setOpenEnroll(false)}>
              <StartProgramModal
                program={program}
                loading={loadingStartProgram}
                message={message}
                signUp={signUp}
              />
            </Modal>

            <Modal isOpen={openCompleted} onClose={() => setOpenCompleted(false)}>
              <ProgramCompleteModal
                message={message}
                program={program}
                rating={programReview.rating}
                selectRating={(rating) => setProgramReview((prev) => ({ ...prev, rating }))}
                review={programReview.review}
                onChangeReview={(e) => setProgramReview((prev) => ({ ...prev, review: e.target.value }))}
                handleCreateReview={handleCreateReview}
              />
            </Modal>
          </div>
        </div>
      </div>}
    </div>
  );
}
